import { ITEM_DO_RODAPE_DA_GAVETA } from '@/components/layout/itemDaGaveta';
import { Link, useLocation } from 'react-router-dom';
import { SinoDeNotificacoes } from '@/features/notificacoes/SinoDeNotificacoes';
import {
    HomeIcon,
    BookOpenIcon,
    QuestionMarkCircleIcon,
    AcademicCapIcon,
    ChartBarIcon,
    UserGroupIcon,
    BuildingOfficeIcon,
    ClipboardDocumentListIcon,
    ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import SairDaConta from '@/components/layout/SairDaConta';
import { BotaoDeRelato } from '@/features/relatos/components/RelatarAlgo';
import { BotaoDeRecolherMenu, useMenuRecolhido } from '@/components/layout/menuRecolhido';
import { useCuradoriaAbertos, classeDoNumeroDaCuradoria } from '@/features/quiz/api/curadoriaAbertos';
import { plural, palavraNoPlural } from '@/utils/plural';

interface TeacherSidebarProps {
    variant?: 'sidebar' | 'drawer';
    onNavigate?: () => void;
}

const TeacherSidebar = ({ variant = 'sidebar', onNavigate }: TeacherSidebarProps) => {
    const location = useLocation();
    const [isReportsOpen] = useState(false);   // a gaveta não tem mais acordeão (seções + a página de relatórios)
    // Recolhida só a barra FIXA: a gaveta do celular nunca vira faixa de ícones (ver `menuRecolhido`).
    const recolhido = useMenuRecolhido() && variant === 'sidebar';

    /** Ativo pelo caminho e pelos que moram dentro dele — a página de detalhe acende o item (`/teacher/classes/12`). */
    const isActive = (path: string, tambem: string[] = []) => {
        if (path === '/teacher') {
            return location.pathname === path;
        }
        return [path, ...tambem].some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
    };

    const isReportsActive = () => {
        return location.pathname.startsWith('/teacher/reports') || location.pathname.startsWith('/teacher/relatorios');
    };

    /*
     * O MENU EM DUAS SEÇÕES E UM RODAPÉ (26/10/2026, reorganização pedida pelo pesquisador). Eram 12 itens em 4 seções.
     * "Turmas e alunos" junta as duas listas em abas (`PaginaComAbas`), e acende também na página da turma e na do aluno,
     * que ficam nos endereços de sempre. "Classificação" junta Disciplinas, Conteúdos e Habilidades (Saeb, BNCC, Enem).
     * Registros de intervenção vai para o dia a dia: é dado que o professor cria, não relatório (o sistema não os cruza
     * com os números). Escolas é consulta rara — cadastro de uma vez —, e desce para o rodapé do menu.
     */
    type Item = { path: string; label: string; icon: typeof HomeIcon; tambem?: string[] };
    const secoesDaGaveta: Array<{ titulo: string; itens: Item[] }> = [
        { titulo: 'Dia a dia', itens: [
            { path: '/teacher', label: 'Painel', icon: HomeIcon },
            { path: '/teacher/simulations', label: 'Simulados', icon: AcademicCapIcon },
            { path: '/teacher/relatorios', label: 'Relatórios', icon: ChartBarIcon },
            { path: '/teacher/turmas-e-alunos', label: 'Turmas e alunos', icon: UserGroupIcon, tambem: ['/teacher/classes', '/teacher/students'] },
            { path: '/teacher/interventions', label: 'Registros de intervenção', icon: ClipboardDocumentListIcon },
        ] },
        { titulo: 'Acervo', itens: [
            { path: '/teacher/questions', label: 'Questões', icon: QuestionMarkCircleIcon },
            { path: '/teacher/classificacao', label: 'Classificação', icon: BookOpenIcon },
            { path: '/teacher/curadoria', label: 'Curadoria', icon: ExclamationTriangleIcon },
        ] },
    ];
    const escolas: Item = { path: '/teacher/minha-escola', label: 'Escolas', icon: BuildingOfficeIcon, tambem: ['/teacher/schools'] };
    const curadoriaAbertos = useCuradoriaAbertos();



    /**
     * A ROLAGEM FUNCIONAVA E NAO SE ANUNCIAVA.
     *
     * Com o submenu de Relatorios aberto em janela de notebook, cabem dois itens
     * e o corte e limpo — `overflow-y-auto` corta limpo, e a barra de rolagem do
     * macOS so aparece durante o gesto. Do lado de quem olha, "Relatorios" tem
     * duas opcoes. O engano e pior que a inconveniencia: quem nao sabe que
     * "Mapa de Descritores" existe nao vai procura-lo.
     *
     * `temMaisAbaixo` acende um desvanecimento na borda inferior quando ha
     * conteudo cortado, e o apaga ao chegar ao fim. Recalculado na rolagem, no
     * redimensionamento e quando o submenu abre ou fecha — os tres momentos em
     * que a resposta muda.
     */
    const navRef = useRef<HTMLElement | null>(null);
    const [temMaisAbaixo, setTemMaisAbaixo] = useState(false);

    const conferirCorte = useCallback(() => {
        const n = navRef.current;
        if (!n) return;
        // 1px de folga: alturas fracionarias fariam a sombra piscar no fim.
        setTemMaisAbaixo(n.scrollTop + n.clientHeight < n.scrollHeight - 1);
    }, []);

    useLayoutEffect(() => {
        conferirCorte();
        const n = navRef.current;
        if (!n) return;
        // `ResizeObserver` e nao `window.resize`: a altura do nav muda tambem
        // quando o submenu abre, sem a janela mudar de tamanho.
        const ro = new ResizeObserver(conferirCorte);
        ro.observe(n);
        return () => ro.disconnect();
    }, [conferirCorte]);

    useEffect(() => { conferirCorte(); }, [isReportsOpen, conferirCorte]);

    /**
     * Ao abrir o submenu, levar o item ATIVO para a vista.
     *
     * Serve a quem ja esta numa tela de relatorio: abre o menu e ve onde esta,
     * em vez de dois itens quaisquer.
     *
     * `block: 'nearest'` E CONDICAO, nao preferencia. Com `'center'` a lista se
     * moveria mesmo quando o item ja estivesse visivel — mover a lista sob o
     * dedo de quem acabou de clicar e pior do que nao fazer nada. `'nearest'`
     * so rola quando precisa.
     */
    const itemAtivoRef = useRef<HTMLAnchorElement | null>(null);
    /**
     * Qual item do submenu esta ativo — o de caminho MAIS LONGO que casa.
     *
     * `isActive` usa `startsWith`, entao em `/teacher/reports/item-responses`
     * casam DOIS itens: "Por Simulado" (`/teacher/reports`) e o verdadeiro. Sem
     * escolher explicitamente, a `ref` ficaria com o ULTIMO que casasse na ordem
     * do array — funciona hoje por acidente de ordenacao, e quebraria no dia em
     * que alguem reordenasse a lista.
     *
     * (O efeito visual de dois itens azuis ao mesmo tempo tambem existe, e e
     * outra coisa: nao foi tocado aqui.)
     */

    useEffect(() => {
        if (!isReportsOpen) return;
        itemAtivoRef.current?.scrollIntoView({ block: 'nearest' });
    }, [isReportsOpen]);

    /**
     * `flex flex-col` NA RAIZ DO DESKTOP — a condição que faltava.
     *
     * ═══════════════════════════════════════════════════════════════════════
     * A PROTEÇÃO JÁ ESTAVA ESCRITA, E NUNCA OPEROU
     * ═══════════════════════════════════════════════════════════════════════
     *
     * O `<nav>` abaixo tem `flex-1 overflow-y-auto` desde sempre: é a
     * assinatura da rolagem interna, escrita de propósito. Só que a raiz do
     * desktop era `h-screen` sem contexto flex — `display: block`, medido.
     *
     * Fora de um flex container, `flex-1` não faz nada. O `<nav>` crescia até o
     * conteúdo, `overflow-y-auto` nunca disparava porque nada restringia a
     * altura, e o branco da raiz parava em 100vh enquanto o menu continuava.
     * Com o submenu de Relatórios aberto — dez itens —, `nav` mede 980px:
     *
     *   janela 900px   156px de menu sobre o cinza da página
     *   janela 768px   288px
     *   janela 600px   456px  (e 12px já com o submenu FECHADO)
     *
     * A variante `drawer` sempre teve `flex flex-col`. Esta não tinha, e a
     * diferença entre as duas era invisível na leitura: as classes do `<nav>`
     * prometem a mesma coisa nos dois casos.
     *
     * Rolagem INTERNA e não `min-h-screen`: menu é referência, não leitura.
     * Fazê-lo empurrar a página o transforma em conteúdo, e aí rolar para baixo
     * às vezes avança o relatório e às vezes revela o resto do menu. Ver achado
     * 90.
     */
    /**
     * `h-full` na variante gaveta — a peça que faltava (achado 96).
     *
     * O invólucro no `TeacherLayout` é `flex-1 overflow-hidden`: ele já limita a
     * altura ao que sobra do painel. Mas é um `div` comum, não um flex
     * container, então esta raiz — filha em fluxo normal — tinha altura `auto` e
     * crescia até o conteúdo: 980px dentro de um invólucro de ~780px.
     *
     * O excedente não rolava, era APARADO. O `overflow-y-auto` do `<nav>` abaixo
     * nunca entrava em ação, porque o pai dele já estava na altura do conteúdo e
     * não havia excedente ali para rolar. Sete dos dez relatórios ficavam fora do
     * alcance, sem barra e sem indício.
     *
     * `h-full` prende a raiz à altura do invólucro; aí o `flex-1` do `<nav>` tem
     * contra o que resolver, e a rolagem interna passa a operar.
     */
    // NA GAVETA, UMA LISTA SÓ QUE ROLA INTEIRA (24/10/2026, pedido do pesquisador — "dois blocos se sobrepõem"): antes o
    // `<nav>` rolava por dentro e o rodapé (Notificações, Relatar algo, Minha conta, Trocar de conta, Sair) ficava FIXO
    // embaixo, com ~250 px. Num celular de 780 px a navegação ficava cortada rente ao rodapé, sem divisória nem sinal de
    // que havia mais — os itens pareciam um por cima do outro. Agora a ordem é a da leitura: navegação → Notificações e
    // Relatar algo → Minha conta, Trocar de conta → Sair, o último; e quem rola é a gaveta. A barra do computador não muda.
    const rootClass = variant === 'drawer'
        ? 'w-full h-full bg-white min-h-0 flex flex-col relative overflow-y-auto overscroll-contain'
        // `max-h-screen` e não `h-screen`: a barra passa a ter a altura do que há
        // nela, limitada à janela, em vez de forçar 100vh a partir de um ponto
        // que não é o topo da janela. Com o `sticky` do `TeacherLayout`, ela
        // acompanha a rolagem e para no topo — e o `overflow-y-auto` do `<nav>`
        // continua valendo quando o menu não couber.
        : `${recolhido ? 'w-16' : 'w-64'} bg-white shadow-sm max-h-screen flex flex-col relative`;
    const linkClass = (active: boolean) =>
        `relative block w-full flex items-center ${recolhido ? 'justify-center px-0' : 'px-4'} py-2 text-sm min-h-[44px] ${active ? 'bg-marca-50 text-marca-600' : 'text-gray-600 hover:bg-gray-50'}`;
    const iconClass = `h-5 w-5 flex-shrink-0 ${recolhido ? '' : 'mr-3'}`;

    return (
        <div className={rootClass}>
            <div className={`flex-shrink-0 flex items-center ${recolhido ? 'justify-center p-2' : 'justify-between gap-2 p-4'}`}>
                {/* Na gaveta o nome do portal saiu: a barra de cima já o tem (estava duplicado). */}
                {/* Sem o "Portal Simulados" (24/10/2026): a barra do topo já o tem; aqui fica só o recolher, à direita. */}
                {!recolhido && variant === 'sidebar' && <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Menu</span>}
                {variant === 'sidebar' && <BotaoDeRecolherMenu recolhido={recolhido} />}
            </div>
            {/* Invólucro relative do que rola — o indício de corte se ancora nele. */}
            <div className={variant === 'drawer' ? 'relative flex-none flex flex-col' : 'relative flex-1 min-h-0 flex flex-col'}>
            <nav
                ref={navRef}
                onScroll={conferirCorte}
                className={`${recolhido ? 'mt-1' : 'mt-4'} ${variant === 'drawer' ? 'flex-none' : 'flex-1 overflow-y-auto'} flex flex-col`}
            >

                {/*
                  RELATÓRIOS: painel em voo no desktop, acordeão na gaveta.

                  ══════════════════════════════════════════════════════════════
                  POR QUE DOIS COMPORTAMENTOS
                  ══════════════════════════════════════════════════════════════

                  O acordeão vertical empurra dez itens para baixo e obriga o
                  `<nav>` a rolar — o indício de corte resolve o engano de "só
                  há duas opções", não a causa. No desktop há espaço AO LADO, e
                  o painel o usa sem deslocar nada.

                  Na gaveta não há lado: ela já ocupa a largura útil. E em toque
                  não existe passagem de ponteiro — o padrão de submenu ali é o
                  acordeão, que é o que a gaveta já faz.

                  ══════════════════════════════════════════════════════════════
                  O PAINEL PRECISA DE PORTAL, E NÃO É DETALHE
                  ══════════════════════════════════════════════════════════════

                  O gatilho vive DENTRO do `<nav>`, que tem `overflow-y-auto`
                  desde o achado 90. Qualquer painel posicionado em relação a ele
                  seria RECORTADO pelo próprio contêiner que rola. `PopoverPanel`
                  com `anchor` renderiza em portal e resolve isso — é a razão de
                  usar o headlessui aqui em vez de um `absolute` à mão.

                  CLIQUE, NÃO PASSAGEM: com dez itens verticais o ponteiro
                  ATRAVESSA "Relatórios" a caminho de Alunos ou Turmas, e um
                  painel de 28rem abrindo sozinho no caminho é ruído. Além disso
                  passagem não existe em toque nem em teclado — o clique
                  precisaria funcionar de qualquer forma, e aí a passagem é
                  superfície a mais para o mesmo resultado.

                  ATENÇÃO A QUEM MEXER: são DUAS variantes do mesmo componente,
                  mantidas em paralelo. Foi exatamente a divergência entre elas
                  que produziu o achado 90 — `flex flex-col` existia numa e não
                  na outra. Ao mudar o submenu, OLHAR OS DOIS RAMOS. Ver achado 92.
                */}
                {/* AS SEÇÕES NOS DOIS (24/10/2026, revisão do pesquisador): a barra do computador mostra as MESMAS seções da gaveta
                    — Dia a dia, Acervo, Escolas, Acompanhamento — e "Relatórios" é um item só, que abre a página de relatórios.
                    Recolhida, só os ícones (o nome em `title`), sem os títulos das seções. */}
                <div className="pb-2" data-testid={variant === 'drawer' ? 'gaveta-em-secoes' : 'menu-em-secoes'}>
                    {secoesDaGaveta.map((secao) => (
                        <div key={secao.titulo} className={recolhido ? 'mt-2 border-t border-gray-100 pt-2 first:mt-0 first:border-0 first:pt-0' : 'mt-3 first:mt-0'}>
                            {!recolhido && <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{secao.titulo}</p>}
                            {secao.itens.map((item) => {
                                const Icon = item.icon;
                                const ativo = item.path === '/teacher/relatorios' ? isReportsActive() : isActive(item.path, item.tambem);
                                const numero = item.path === '/teacher/curadoria' ? curadoriaAbertos : 0;
                                return (
                                    <Link key={item.path} to={item.path} onClick={onNavigate} className={linkClass(ativo)} title={recolhido ? item.label : undefined} aria-current={ativo ? 'page' : undefined} data-testid={`menu-${item.path}`}>
                                        <Icon className={iconClass} />
                                        <span className={recolhido ? 'sr-only' : undefined}>{item.label}</span>
                                        {numero > 0 && (
                                            <span className={classeDoNumeroDaCuradoria(recolhido)} data-testid="numero-da-curadoria" title={recolhido ? plural(numero, 'achado aberto', 'achados abertos') : undefined}>
                                                {recolhido ? <span className="sr-only">{plural(numero, 'achado aberto', 'achados abertos')}</span> : numero}
                                                {!recolhido && <span className="sr-only"> {palavraNoPlural(numero, 'achado aberto', 'achados abertos')}</span>}
                                            </span>
                                        )}
                                    </Link>
                                );
                            })}
                        </div>
                    ))}
                    {/* O RODAPÉ DO MENU: Escolas, sem título de seção, depois de um fio. */}
                    <div className={`mt-3 border-t border-gray-100 pt-2`} data-testid="rodape-do-menu">
                        <Link to={escolas.path} onClick={onNavigate} className={linkClass(isActive(escolas.path, escolas.tambem))} title={recolhido ? escolas.label : undefined} aria-current={isActive(escolas.path, escolas.tambem) ? 'page' : undefined} data-testid={`menu-${escolas.path}`}>
                            <BuildingOfficeIcon className={iconClass} />
                            <span className={recolhido ? 'sr-only' : undefined}>{escolas.label}</span>
                        </Link>
                    </div>
                </div>
            </nav>

            {/*
              O indicio de que ha mais abaixo.

              Gradiente do branco ao transparente, ancorado na borda inferior da
              barra. `pointer-events-none` e essencial: ele cobre o ultimo item
              parcialmente visivel, e sem isso engoliria o clique nele.

              `aria-hidden` porque nao ha nada a anunciar a quem usa leitor de
              tela — para esse, a lista inteira ja esta no DOM e sempre esteve; o
              que faltava era pista VISUAL, e so.

              Aparece com o submenu aberto em janela baixa e some ao rolar ate o
              fim. Em Windows, onde a barra de rolagem fica sempre visivel, e
              redundante — e sombra a mais nao atrapalha ninguem; o relato veio
              do Mac, onde a barra some.
            */}
                {/* O indício de corte é ancorado ao INVÓLUCRO do `<nav>`, não à
                    raiz: com o rodapé de Sair abaixo, um `bottom-0` da raiz
                    cairia sobre o botão e o esmaeceria. */}
                {temMaisAbaixo && (
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent"
                    />
                )}
            </div>
            {/* Sair só na GAVETA. No desktop a barra do topo do `MainLayout` já
                o traz, e os dois apareceriam juntos — ver a nota do rodapé. */}
            {/*
                O CANAL DE RELATO NO CELULAR.
                A barra do topo do `MainLayout` traz o botão, mas ela é
                `hidden md:flex` — abaixo de 768px ele some. E a gaveta do
                `MainLayout` não abre aqui, porque esta rota tem gaveta
                própria (`temGavetaPropria`). Sem esta linha, o professor no
                aplicativo ficava SEM canal em toda a área dele, que é onde
                ele passa o tempo todo.
            */}
            {variant === 'drawer' && (
              <div className="flex-shrink-0 px-2">
                <SinoDeNotificacoes variante="item" aoNavegar={onNavigate} />
              </div>
            )}
            {variant === 'drawer' && (
                <div className="flex-shrink-0 px-2 pb-1">
                    <BotaoDeRelato className={ITEM_DO_RODAPE_DA_GAVETA} />
                </div>
            )}
            {variant === 'drawer' && <SairDaConta aoSair={onNavigate} />}
        </div>
    );
};

export default TeacherSidebar; 